/** The fields asset resolution reads -- satisfied by a `Component` and by the flat item shape the 3D scene manifest carries. */
export type ComponentAssetSource = {
	sku?: string | null;
	fuelFamily?: string | null;
	modelFile?: string | null;
	imageFile?: string | null;
};

const MODEL_EXTENSION = '.dae';
const IMAGE_EXTENSION = '.png';

const withExtension = (file: string, extension: string): string => ((/\.[a-z0-9]+$/iu).test(file) ? file : `${file}${extension}`);

const resolveAssetBase = (source: ComponentAssetSource | null | undefined, override: string | null | undefined): string => {
	const explicit = (override ?? '').trim();
	if (explicit !== '') return explicit;
	// a fuel variant (C3SL36LP/NG) shares its family's artwork -- the catalog row was split per fuel, the assets were not
	return source?.fuelFamily || source?.sku || '';
};

/**
 * Resolves the 3D model file name for a component, e.g. `C3SL36.dae`.
 * `modelFile` wins, then the fuel family, then the sku. The extension is appended only when absent,
 * so a `.glb` model can be named outright.
 * @param source component (or scene-manifest item) to resolve
 * @returns file name, or an empty string when nothing resolves
 */
export const getComponentModelFileCfn = (source: ComponentAssetSource | null | undefined): string => {
	const base = resolveAssetBase(source, source?.modelFile);
	return base === '' ? '' : withExtension(base, MODEL_EXTENSION);
};

/**
 * Resolves the product image file name for a component, e.g. `C3SL36.png`.
 * `imageFile` wins, then the fuel family, then the sku.
 * @param source component (or scene-manifest item) to resolve
 * @returns file name, or an empty string when nothing resolves
 */
export const getComponentImageFileCfn = (source: ComponentAssetSource | null | undefined): string => {
	const base = resolveAssetBase(source, source?.imageFile);
	return base === '' ? '' : withExtension(base, IMAGE_EXTENSION);
};
